import { identityOf, type Identity } from "@/design-system/identity.ts"

/*
 * A Member as the web app names them: by the name the web API gives with every Member, their
 * Profile's first and last name, else their email, else their username (`nameOf` in
 * `@beacon/core/access/name-of`). An avatar's initials come from that name, and its marker colour
 * from their Member id, so neither changes when their email does.
 */

const separators = /[._-]+/

/** The part of an email before its `@`, or the whole of it when it has none. */
export function localPartOf(email: string) {
  const at = email.lastIndexOf("@")
  return at === -1 ? email : email.slice(0, at)
}

/**
 * A Member as a line names them, or "A former Member" for one who is gone from the record (`null`).
 * One deactivated (`former`) is still named, and marked as former.
 */
export const nameShown = (person: { name: string } | null, former = false) =>
  person ? (former ? `${person.name} (former)` : person.name) : "A former Member"

const firstLetter = (word: string) => Array.from(word)[0] ?? ""

/**
 * Up to two letters for an avatar: the first of their first and last names (Thandi Mokoena is TM),
 * or the first of a single name. A name that is an email, for someone with no Profile name, gives
 * the first letters of its local part's first two words (`sean.m` is SM), or the first two letters
 * of a local part with only one (`amagut` is AM).
 */
export function initialsOf(name: string) {
  if (name.includes("@")) {
    const [first = "", second] = localPartOf(name).split(separators).filter(Boolean)
    const initials = second === undefined ? first.slice(0, 2) : `${first[0]}${second[0]}`
    return initials.toUpperCase()
  }
  const words = name.trim().split(/\s+/).filter(Boolean)
  const first = words[0] ?? ""
  const last = words.length > 1 ? words.at(-1)! : ""
  return `${firstLetter(first)}${firstLetter(last)}`.toUpperCase()
}

/** A Member's marker colour, hashed from their id. */
export const markerOf = ({ id }: { id: string }): Identity => identityOf(id)

/**
 * The name a greeting uses: their Profile's first name, else the first word of their email,
 * capitalised (`sean.m` is Sean).
 */
export function firstNameOf({ firstName, email }: { firstName: string | null; email: string }) {
  if (firstName?.trim()) return firstName.trim()
  const [first = localPartOf(email)] = localPartOf(email).split(separators).filter(Boolean)
  return first.charAt(0).toUpperCase() + first.slice(1)
}

/** Text as names are compared as someone types: accents taken off, lower case. */
const folded = (text: string) => text.normalize("NFD").replace(/\p{M}/gu, "").toLowerCase()

/**
 * How what someone typed finds a Member: by `name` when a word of their name starts with it (or the
 * whole name does), by `email` when only their email holds it, or not at all. Case and accents
 * don't matter.
 */
export function matchesName(
  { name, email }: { name: string; email?: string | null },
  typed: string,
): "name" | "email" | false {
  const query = folded(typed.trim())
  if (!query) return "name"
  const named = folded(name)
  if (named.startsWith(query) || named.split(/\s+/).some((word) => word.startsWith(query)))
    return "name"
  return email && folded(email).includes(query) ? "email" : false
}

/**
 * The Members what someone typed finds, by name first, then by email, each kept in the order given.
 * Nothing typed keeps everyone.
 */
export function rankedByName<M extends { name: string; email?: string | null }>(
  members: readonly M[],
  typed: string,
): M[] {
  const byName = members.filter((member) => matchesName(member, typed) === "name")
  const byEmail = members.filter((member) => matchesName(member, typed) === "email")
  return [...byName, ...byEmail]
}

/** A zone's offset from UTC now, in minutes east; none for a zone this browser doesn't know. */
function offsetMinutes(zone: string, now: Date) {
  try {
    const name =
      new Intl.DateTimeFormat("en", { timeZone: zone, timeZoneName: "longOffset" })
        .formatToParts(now)
        .find(({ type }) => type === "timeZoneName")?.value ?? ""
    const [, sign = "+", hours = "0", minutes = "0"] = /GMT([+-])(\d+):?(\d+)?/.exec(name) ?? []
    return (sign === "-" ? -1 : 1) * (Number(hours) * 60 + Number(minutes))
  } catch {
    return undefined
  }
}

/**
 * What time it is where someone is, from their Profile's time zone, and how far that is from the
 * viewer's own (`viewerZone`, the browser's when they've set none): "14:30", "6h ahead of you".
 * None for someone with no time zone, or one this browser doesn't know.
 */
export function localTimeOf(timeZone: string | null, now: Date, viewerZone: string) {
  if (!timeZone) return undefined
  const theirs = offsetMinutes(timeZone, now)
  const yours = offsetMinutes(viewerZone, now)
  if (theirs === undefined || yours === undefined) return undefined
  const time = new Intl.DateTimeFormat("en-GB", {
    hour: "2-digit",
    minute: "2-digit",
    hourCycle: "h23",
    timeZone,
  }).format(now)
  const apart = theirs - yours
  const size = Math.abs(apart)
  const span = [Math.floor(size / 60) && `${Math.floor(size / 60)}h`, size % 60 && `${size % 60}m`]
    .filter(Boolean)
    .join(" ")
  return {
    time,
    difference:
      apart === 0 ? "Same time as you" : `${span} ${apart > 0 ? "ahead of" : "behind"} you`,
  }
}

/**
 * Whether what someone typed on the People page finds a person: every word of it found in their
 * name, an email, their job title or their location. Case and accents don't matter, and nothing
 * typed finds everyone.
 */
export function matchesPerson(
  person: {
    name: string
    jobTitle?: string | null
    location?: string | null
    companyEmails?: readonly string[]
  },
  typed: string,
) {
  const haystack = folded(
    [person.name, person.jobTitle, person.location, ...(person.companyEmails ?? [])]
      .filter(Boolean)
      .join(" "),
  )
  return folded(typed)
    .split(/\s+/)
    .filter(Boolean)
    .every((word) => haystack.includes(word))
}

/** Each kind a merge moves, as its preview names it: one, and many. */
const mergeKinds = {
  identities: ["email or account", "emails and accounts"],
  groups: ["Group", "Groups"],
  grants: ["grant", "grants"],
  assignments: ["assigned ticket", "assigned tickets"],
  authored: ["ticket, comment or file they made", "tickets, comments and files they made"],
  changes: ["change they made", "changes they made"],
  spend: ["model call", "model calls"],
  knowledge: ["knowledge base item or feed", "knowledge base items and feeds"],
  accountsAndTokens: ["connector account, agent or token", "connector accounts, agents and tokens"],
  drafts: ["draft", "drafts"],
  personalProject: ["ticket in their Personal Project", "tickets in their Personal Project"],
} as const

/**
 * What a merge would move, as its preview lists it: each kind with something in it, counted ("3
 * Groups"), in a fixed order; nothing at all is one line saying so.
 */
export function describeMergeCounts(counts: Record<keyof typeof mergeKinds, number>) {
  const lines = (Object.keys(mergeKinds) as (keyof typeof mergeKinds)[]).flatMap((kind) => {
    const count = counts[kind]
    const [one, many] = mergeKinds[kind]
    return count > 0 ? [`${count} ${count === 1 ? one : many}`] : []
  })
  return lines.length ? lines : ["Nothing but their name and Profile"]
}
